import { subMonths, isAfter, subHours } from 'date-fns';
import { useCallback } from 'react';

import { STORAGE_KEYS } from '@/constants/storageKeys';
import { isSigningOut } from '@/services/auth';
import { getInquiriesV2 } from '@/services/getInquiriesV2';
import { getPlatformStorage } from '@/util/platformStorage';
import { Sentry } from '@/util/sentry';

/**
 * New user window for showing demo modal
 */
const NEW_USER_WINDOW_MONTHS = 1;

/**
 * Remind me later delay (24 hours)
 */
const REMIND_LATER_DELAY_HOURS = 24;

/**
 * Custom hook for managing inquiry demo modal state and storage
 *
 * Provides functions to track whether the demo modal has been shown to a user,
 * mark it as permanently dismissed, or defer it for later display.
 *
 * Storage Strategy:
 * - Uses AsyncStorage on mobile, sessionStorage on web (when no userId)
 * - Tracks permanent dismissal with 'primum:inquiry-demo:shown'
 * - Tracks 24-hour deferral with 'primum:inquiry-demo:show-later'
 *
 * Logic:
 * - Checks if demo was permanently dismissed first
 * - Then checks if user deferred and 24 hours haven't passed
 * - Finally checks if account is less than 1 month old
 * - Returns false if any condition prevents showing
 *
 * Error Handling:
 * - Logs errors to console and captures in Sentry
 * - Returns false on any storage errors to prevent modal display
 * - Skips API calls during logout to prevent 401 errors (tokens cleared before components unmount)
 */

export const useInquiryDemoModal = (userId?: string) => {
  const getStorage = (userId?: string) => getPlatformStorage(userId, 'local');

  const markShown = useCallback(async () => {
    if (!userId) {
      return;
    }

    try {
      await getStorage(userId).setItem(
        STORAGE_KEYS.ONBOARDING_INQUIRY_DEMO_SHOWN,
        new Date().toISOString()
      );
    } catch (e) {
      console.error(e);

      Sentry.captureException(e);
    }
  }, [userId]);

  const markShowLater = useCallback(async () => {
    if (!userId) {
      return;
    }

    try {
      await getStorage(userId).setItem(
        STORAGE_KEYS.INQUIRY_DEMO_ONBOARDING_DEFERRED_UNTIL,
        new Date().toISOString()
      );
    } catch (e) {
      console.error(e);

      Sentry.captureException(e);
    }
  }, [userId]);

  const shouldShow = useCallback(
    async (userData?: any): Promise<boolean> => {
      if (!userId) {
        return false;
      }

      try {
        const demoShown = await getStorage(userId).getItem(
          STORAGE_KEYS.ONBOARDING_INQUIRY_DEMO_SHOWN
        );

        if (demoShown) {
          return false;
        }

        const showLater = await getStorage(userId).getItem(
          STORAGE_KEYS.INQUIRY_DEMO_ONBOARDING_DEFERRED_UNTIL
        );

        if (showLater) {
          const showLaterTime = new Date(showLater);

          const remindLaterDeadline = subHours(new Date(), REMIND_LATER_DELAY_HOURS);

          if (isAfter(showLaterTime, remindLaterDeadline)) {
            return false;
          }
        }

        try {
          if (isSigningOut()) {
            return false;
          }

          const userInquiries = await getInquiriesV2();

          if (userInquiries?.inquiries && userInquiries.inquiries.length > 0) {
            return false;
          }
        } catch (error) {
          console.error('Error checking user inquiries:', error);
        }

        if (!userData?.created_at) {
          return true;
        }

        const userCreatedAt = new Date(userData.created_at);

        const oneMonthAgo = subMonths(new Date(), NEW_USER_WINDOW_MONTHS);

        return isAfter(userCreatedAt, oneMonthAgo);
      } catch (e) {
        console.error(e);

        Sentry.captureException(e);

        return false;
      }
    },
    [userId]
  );

  return {
    markShown,
    markShowLater,
    shouldShow,
  };
};
